import type { RegistryItem } from "@schemas/calculationmethods/schema";
import { CalculationMethodType, TierPriceUnit, TierUnit } from "__generated__/graphql";
import TieredCalculationMethodForm from "view/calculationmethods/components/form/TieredCalculationMethodForm";
import TieredCalculationMethodDetails from "view/calculationmethods/components/details/TieredCalculationMethodDetails";
import { isTankmatch } from "components/utils/Permission";

export const TieredSchema: RegistryItem = {
	[CalculationMethodType.Tiered]: {
		Form: TieredCalculationMethodForm,
		Details: TieredCalculationMethodDetails,
		fields: {
			"calculationMethod.tierUnit": { path: "tierUnit", rules: { required: true } },
			"calculationMethod.cargoQuantityUnit": { path: "cargoQuantityUnit", rules: { requiredIf: ({ calculationMethod }) => calculationMethod?.tierUnit === TierUnit.CargoQuantity } },
			"calculationMethod.payableOver": { path: "payableOver", rules: { requiredIf: ({ calculationMethod }) => calculationMethod?.tierUnit === TierUnit.CargoQuantity } },
			"calculationMethod.waterLevelLocation": {
				path: "waterLevelLocation",
				rules: {
					requiredIf: ({ calculationMethod }) => calculationMethod?.tierUnit === TierUnit.WaterLevel,
				},
			},
			"calculationMethod.referenceRateDateType": {
				path: "referenceRateDateType",
				rules: {
					requiredIf: ({ calculationMethod }) => calculationMethod?.tierUnit === TierUnit.GasoilPrice,
				},
			},
			"calculationMethod.tiers": {
				path: "tiers",
				rules: { required: true },
			},
			"calculationMethod.applicableAgreements": {
				path: "applicableAgreements",
				rules: {
					requiredIf: ({ calculationMethod }) => {
						const hasPercentageTier = calculationMethod?.tiers?.some(t => t.priceUnit === TierPriceUnit.SurchargePercentage);
						if (!hasPercentageTier) {
							return false;
						}
						return !isTankmatch();
					},
				},
			},
			"calculationMethod.applicableAgreementTypes": {
				path: "applicableAgreementTypes",
				rules: {
					requiredIf: ({ calculationMethod }) => {
						const hasPercentageTier = calculationMethod?.tiers?.some(t => t.priceUnit === TierPriceUnit.SurchargePercentage);
						if (!hasPercentageTier) {
							return false;
						}
						return isTankmatch();
					},
				},
			},
		},
		toInput: (form) => ({
			tieredCalculationMethodInput: {
				id: form.id,
				tierUnit: form.tierUnit,
				payableOver: form.payableOver,
				cargoQuantityUnit: form.tierUnit === TierUnit.CargoQuantity ? form.cargoQuantityUnit : undefined,
				waterLevelLocation: form.tierUnit === TierUnit.WaterLevel ? form.waterLevelLocation : undefined,
				referenceRateDateType: form.tierUnit === TierUnit.GasoilPrice ? form.referenceRateDateType : undefined,
				cdniCostIncluded: form.tierUnit === TierUnit.GasoilPrice ? form.cdniCostIncluded : undefined,
				tiers: form.tiers?.map(x => ({
					id: x.id,
					priceUnit: x.priceUnit,
					price: x.price ?? 0,
					pricePerMtAdditionalToLumpsum: form.tierUnit === TierUnit.CargoQuantity ? x.pricePerMtAdditionalToLumpsum : undefined,
					minimum: x.minimum ?? 0,
					maximum: x.maximum ?? 0,
				})) ?? [],
				applicableAgreementTypes: form.applicableAgreementTypes ?? [],
				applicableAgreements: form.applicableAgreements?.map(a => ({ id: a.id, uuid: a.uuid })),
			},
		}),
		toForm: (method) => ({
			tierUnit: method.tierUnit ?? undefined,
			cargoQuantityUnit: method.cargoQuantityUnit ?? undefined,
			waterLevelLocation: method.waterLevelLocation ?? undefined,
			referenceRateDateType: method.referenceRateDateType ?? undefined,
			cdniCostIncluded: method.cdniCostIncluded ?? false,
			tiers: method.tiers?.map(x => ({
				id: x.id ?? null,
				priceUnit: x.priceUnit ?? null,
				price: x.price ?? null,
				pricePerMtAdditionalToLumpsum: x.pricePerMtAdditionalToLumpsum ?? null,
				minimum: x.minimum ?? null,
				maximum: x.maximum ?? null,
			})) ?? [],
			applicableAgreementTypes: method.applicableAgreementTypes ?? [],
			applicableAgreements: method.applicableAgreements?.map(a => ({
				id: a.id,
				type: a.type,
				description: a.description ?? undefined,
				calculationMethod: null,
			})) ?? [],
		}),
	},
};
